import type { ApiResponse } from '@/libs/interfaces'
import type { Job, JobListParams, JobListResponse } from '@/services/api/jobs'

import { mockJobs } from '../fixtures/jobs'
import { delay, genID, matchesSearch, nowISO } from '../helpers'
import { createEntityStore } from '../store'

const jobStore = createEntityStore<Job, 'id'>(mockJobs, 'id')

const toApiResponse = <T>(data: T): ApiResponse<T> => ({ data, status: 200 })

const filterJobs = (params: JobListParams = {}): Job[] =>
  jobStore
    .getAll()
    .filter((job) => matchesSearch(job, params.search, ['jobNumber', 'notes']))
    .filter((job) => (params.status ? job.status === params.status : true))
    .filter((job) => (params.customerId ? job.customerId === params.customerId : true))
    .filter((job) => (params.employeeId ? job.employeeId === params.employeeId : true))
    .filter((job) => (params.dateFrom ? job.scheduledDate >= params.dateFrom : true))
    .filter((job) => (params.dateTo ? job.scheduledDate <= params.dateTo : true))

export const jobsApiMock = {
  list: async (params?: JobListParams): Promise<ApiResponse<JobListResponse>> => {
    const filtered = filterJobs(params)
    const page = params?.page ?? 1
    const limit = params?.limit ?? filtered.length
    const startIndex = (page - 1) * limit
    return delay(
      toApiResponse({
        data: filtered.slice(startIndex, startIndex + limit),
        total: filtered.length,
        page,
        limit,
      }),
    )
  },

  getById: async (id: string): Promise<ApiResponse<Job>> =>
    delay(toApiResponse(jobStore.getById(id) ?? ({} as Job))),

  create: async (data: Omit<Job, 'id' | 'jobNumber'>): Promise<ApiResponse<Job>> => {
    const job: Job = {
      id: genID('job'),
      jobNumber: `JOB-${1000 + jobStore.getAll().length + 1}`,
      createdAt: nowISO(),
      updatedAt: nowISO(),
      ...data,
    }
    jobStore.insert(job)
    return delay(toApiResponse(job))
  },

  update: async (id: string, data: Partial<Job>): Promise<ApiResponse<Job>> => {
    const updated = jobStore.update(id, { ...data, updatedAt: nowISO() })
    return delay(toApiResponse(updated ?? ({ id, ...data } as Job)))
  },

  delete: async (id: string): Promise<ApiResponse<void>> => {
    jobStore.remove(id)
    return delay(toApiResponse(undefined))
  },

  updateStatus: async (id: string, status: Job['status']): Promise<ApiResponse<Job>> => {
    const updated = jobStore.update(id, { status, updatedAt: nowISO() })
    return delay(toApiResponse(updated ?? ({ id, status } as Job)))
  },

  assign: async (id: string, employeeId: string): Promise<ApiResponse<Job>> => {
    const updated = jobStore.update(id, { employeeId, updatedAt: nowISO() })
    return delay(toApiResponse(updated ?? ({ id, employeeId } as Job)))
  },
}
